import React, {
  useState, useRef, useCallback, useEffect,
} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  LinearProgress,
  Paper,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Photo as PhotoIcon,
  VideoLibrary as VideoIcon,
  ViewInAr as VirtualTourIcon,
  Map as FloorPlanIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  YouTube as YouTubeIcon,
  Link as LinkIcon,
  DragIndicator as DragIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import VirtualTourIntegration from './VirtualTourIntegration';
import FloorPlanManager from './FloorPlanManager';

function TabPanel({
  children, value, index, ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`media-tabpanel-${index}`}
      aria-labelledby={`media-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function MediaManager({ property, onUpdate }) {
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [fullscreenView, setFullscreenView] = useState(false);
  const [mediaData, setMediaData] = useState({
    photos: [],
    videos: [],
    virtualTours: [],
    floorPlans: [],
  });

  // Mock media data - in real implementation, this would come from API
  const mockMediaData = {
    photos: [
      {
        id: 1,
        url: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800',
        title: 'Exterior Front View',
        description: 'Beautiful front facade with modern design',
        category: 'Exterior',
        featured: true,
        order: 1,
        uploadedAt: new Date('2024-07-15'),
        size: '2.3 MB',
      },
      {
        id: 2,
        url: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800',
        title: 'Living Room',
        description: 'Spacious living room with natural light',
        category: 'Interior',
        featured: false,
        order: 2,
        uploadedAt: new Date('2024-07-15'),
        size: '1.8 MB',
      },
      {
        id: 3,
        url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
        title: 'Kitchen',
        description: 'Modern kitchen with stainless steel appliances',
        category: 'Interior',
        featured: false,
        order: 3,
        uploadedAt: new Date('2024-07-15'),
        size: '2.1 MB',
      },
    ],
    videos: [
      {
        id: 1,
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        title: 'Property Walkthrough',
        description: 'Complete video tour of the property',
        duration: '3:45',
        thumbnail: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400',
        uploadedAt: new Date('2024-07-15'),
      },
    ],
    virtualTours: [
      {
        id: 1,
        url: 'https://my.matterport.com/show/?m=example123',
        title: '3D Virtual Tour',
        description: 'Interactive 3D tour powered by Matterport',
        provider: 'Matterport',
        thumbnail: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400',
        uploadedAt: new Date('2024-07-15'),
      },
    ],
    floorPlans: [
      {
        id: 1,
        url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
        title: 'Main Floor Plan',
        description: 'Detailed floor plan of the main level',
        type: 'Main Floor',
        thumbnail: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400',
        uploadedAt: new Date('2024-07-15'),
      },
    ],
  };

  useEffect(() => {
    if (property) {
      setMediaData(mockMediaData);
    }
  }, [property]);

  const onDrop = useCallback((acceptedFiles) => {
    setUploading(true);
    setUploadProgress(0);

    // Simulate file upload
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setUploading(false);
          setUploadProgress(0);

          // Add uploaded files to media data
          const newPhotos = acceptedFiles.map((file, index) => ({
            id: Date.now() + index,
            url: URL.createObjectURL(file),
            title: file.name.replace(/\.[^/.]+$/, ''),
            description: '',
            category: 'Uncategorized',
            featured: false,
            order: mediaData.photos.length + index + 1,
            uploadedAt: new Date(),
            size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
          }));

          setMediaData((prev) => ({
            ...prev,
            photos: [...prev.photos, ...newPhotos],
          }));

          return 0;
        }
        return prev + 10;
      });
    }, 200);
  }, [mediaData.photos.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.avi', '.webm'],
    },
    multiple: true,
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddMedia = (type) => {
    setSelectedMedia({ type, data: {} });
    setOpenDialog(true);
  };

  const handleEditMedia = (media, type) => {
    setSelectedMedia({ type, data: media });
    setOpenDialog(true);
  };

  const handleDeleteMedia = (mediaId, type) => {
    setMediaData((prev) => ({
      ...prev,
      [type]: prev[type].filter((item) => item.id !== mediaId),
    }));
  };

  const handleSaveMedia = (mediaData) => {
    if (selectedMedia.data.id) {
      // Edit existing media
      setMediaData((prev) => ({
        ...prev,
        [selectedMedia.type]: prev[selectedMedia.type].map((item) => (item.id === selectedMedia.data.id ? { ...item, ...mediaData } : item)),
      }));
    } else {
      // Add new media
      const newMedia = {
        id: Date.now(),
        ...mediaData,
        uploadedAt: new Date(),
      };
      setMediaData((prev) => ({
        ...prev,
        [selectedMedia.type]: [...prev[selectedMedia.type], newMedia],
      }));
    }
    setOpenDialog(false);
  };

  const toggleFeatured = (mediaId) => {
    setMediaData((prev) => ({
      ...prev,
      photos: prev.photos.map((photo) => (photo.id === mediaId ? { ...photo, featured: !photo.featured } : photo)),
    }));
  };

  const renderPhotoGallery = () => (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">
          Photo Gallery (
          {mediaData.photos.length}
          {' '}
          photos)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddMedia('photos')}
        >
          Add Photo
        </Button>
      </Box>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Uploading...
            {' '}
            {uploadProgress}
            %
          </Typography>
        </Box>
      )}

      <Paper
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          mb: 3,
          cursor: 'pointer',
          bgcolor: isDragActive ? 'primary.50' : 'background.paper',
        }}
      >
        <input {...getInputProps()} />
        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop files here' : 'Drag & drop photos here'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to select files
        </Typography>
      </Paper>

      <ImageList cols={3} gap={16}>
        {mediaData.photos.map((photo) => (
          <ImageListItem key={photo.id} sx={{ position: 'relative' }}>
            <img
              src={photo.url}
              alt={photo.title}
              loading="lazy"
              style={{ height: 200, objectFit: 'cover' }}
            />
            <ImageListItemBar
              title={photo.title}
              subtitle={photo.description}
              actionIcon={(
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="View">
                    <IconButton
                      sx={{ color: 'white' }}
                      onClick={() => setSelectedMedia({ type: 'photos', data: photo })}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      sx={{ color: 'white' }}
                      onClick={() => handleEditMedia(photo, 'photos')}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      sx={{ color: 'white' }}
                      onClick={() => handleDeleteMedia(photo.id, 'photos')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            />
            {photo.featured && (
              <Chip
                label="Featured"
                color="primary"
                size="small"
                sx={{ position: 'absolute', top: 8, left: 8 }}
              />
            )}
          </ImageListItem>
        ))}
      </ImageList>
    </Box>
  );

  const renderVideoGallery = () => (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">
          Video Gallery (
          {mediaData.videos.length}
          {' '}
          videos)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddMedia('videos')}
        >
          Add Video
        </Button>
      </Box>

      <Grid container spacing={2}>
        {mediaData.videos.map((video) => (
          <Grid item xs={12} sm={6} md={4} key={video.id}>
            <Card>
              <Box sx={{ position: 'relative' }}>
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  style={{ width: '100%', height: 200, objectFit: 'cover' }}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  }}
                  onClick={() => window.open(video.url, '_blank')}
                >
                  <PlayIcon />
                </IconButton>
              </Box>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {video.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {video.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Duration:
                  {' '}
                  {video.duration}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <IconButton size="small" onClick={() => handleEditMedia(video, 'videos')}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteMedia(video.id, 'videos')}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Media Management
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Photos" icon={<PhotoIcon />} />
        <Tab label="Videos" icon={<VideoIcon />} />
        <Tab label="Virtual Tours" icon={<VirtualTourIcon />} />
        <Tab label="Floor Plans" icon={<FloorPlanIcon />} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        {renderPhotoGallery()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderVideoGallery()}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <VirtualTourIntegration property={property} onUpdate={onUpdate} />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <FloorPlanManager property={property} onUpdate={onUpdate} />
      </TabPanel>

      {/* Media Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMedia?.data.id ? 'Edit Media' : 'Add New Media'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                value={selectedMedia?.data.title || ''}
                onChange={(e) => setSelectedMedia((prev) => ({
                  ...prev,
                  data: { ...prev.data, title: e.target.value },
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={selectedMedia?.data.description || ''}
                onChange={(e) => setSelectedMedia((prev) => ({
                  ...prev,
                  data: { ...prev.data, description: e.target.value },
                }))}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            {selectedMedia?.type === 'photos' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedMedia?.data.category || ''}
                    onChange={(e) => setSelectedMedia((prev) => ({
                      ...prev,
                      data: { ...prev.data, category: e.target.value },
                    }))}
                    label="Category"
                  >
                    <MenuItem value="Exterior">Exterior</MenuItem>
                    <MenuItem value="Interior">Interior</MenuItem>
                    <MenuItem value="Kitchen">Kitchen</MenuItem>
                    <MenuItem value="Bathroom">Bathroom</MenuItem>
                    <MenuItem value="Bedroom">Bedroom</MenuItem>
                    <MenuItem value="Living Room">Living Room</MenuItem>
                    <MenuItem value="Dining Room">Dining Room</MenuItem>
                    <MenuItem value="Basement">Basement</MenuItem>
                    <MenuItem value="Garage">Garage</MenuItem>
                    <MenuItem value="Yard">Yard</MenuItem>
                    <MenuItem value="Uncategorized">Uncategorized</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {selectedMedia?.type === 'videos' && (
              <Grid item xs={12}>
                <TextField
                  label="Video URL (YouTube, Vimeo, etc.)"
                  value={selectedMedia?.data.url || ''}
                  onChange={(e) => setSelectedMedia((prev) => ({
                    ...prev,
                    data: { ...prev.data, url: e.target.value },
                  }))}
                  fullWidth
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="URL"
                value={selectedMedia?.data.url || ''}
                onChange={(e) => setSelectedMedia((prev) => ({
                  ...prev,
                  data: { ...prev.data, url: e.target.value },
                }))}
                fullWidth
                placeholder="https://..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleSaveMedia(selectedMedia.data)}
            variant="contained"
            disabled={!selectedMedia?.data.title || !selectedMedia?.data.url}
          >
            {selectedMedia?.data.id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen View Dialog */}
      <Dialog
        open={fullscreenView && selectedMedia}
        onClose={() => setFullscreenView(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedMedia?.data.title}
          <IconButton
            onClick={() => setFullscreenView(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedMedia && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={selectedMedia.data.url}
                alt={selectedMedia.data.title}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
              <Typography variant="h6" sx={{ mt: 2 }}>
                {selectedMedia.data.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedMedia.data.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default MediaManager;
